import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Two flavours of the same flow live here:
 *   - ``?token=...`` (default): forgot-password reset. Token expires
 *     in an hour, signed under "password-reset".
 *   - ``?token=...&invite=1``: a freshly-invited user choosing their
 *     first password (Phase 2.5.B). Token lasts 7 days, signed under
 *     "user-invite". The headline + button text change so the user
 *     understands they're being onboarded, not recovering an account.
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const isInvite = searchParams.get('invite') === '1';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-card">
          <img src="/logo-icon.png" alt="Huri-Connect" className="login-logo-icon" />
          <h1>קישור לא תקף</h1>
          <p>הקישור שלחצתם עליו אינו מכיל טוקן. ייתכן שהוא נחתך בהעתקה.</p>
          <Link to="/forgot-password" className="login-forgot">בקשת קישור חדש</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('הסיסמה צריכה להיות באורך 8 תווים לפחות');
      return;
    }
    if (!/\d/.test(password)) {
      setError('הסיסמה צריכה לכלול לפחות ספרה אחת');
      return;
    }
    if (password !== confirm) {
      setError('הסיסמאות אינן תואמות');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/auth/reset-password', {
        token,
        password,
        mode: isInvite ? 'invite' : 'reset',
      });
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'אירעה שגיאה, נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = isInvite ? 'ברוכים הבאים ל-Huri-Connect' : 'בחירת סיסמה חדשה';
  const subheading = isInvite ? 'בחרו סיסמה כדי להיכנס לחשבון שלכם.' : null;
  const submitLabel = isInvite ? 'קביעת סיסמה והתחברות' : 'עדכון סיסמה';

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <img src="/logo-icon.png" alt="Huri-Connect" className="login-logo-icon" />
        <h1>{heading}</h1>
        {subheading && <p>{subheading}</p>}
        {error && <div className="login-error">{error}</div>}
        <input
          type="password"
          placeholder={isInvite ? 'בחירת סיסמה' : 'סיסמה חדשה'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        <div className="login-hint">לפחות 8 תווים וספרה אחת.</div>
        <input
          type="password"
          placeholder="אישור סיסמה"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          required
        />
        <button type="submit" disabled={submitting}>
          {submitting ? 'שומר...' : submitLabel}
        </button>
        <Link to="/login" className="login-forgot">חזרה לכניסה</Link>
      </form>
    </div>
  );
}

export default ResetPassword;
